/**
 * Thin API fetch wrapper.
 *
 * In dev: Vite proxy forwards `/api` → `localhost:8080`.
 * In prod: CloudFront forwards `/api` → ALB origin (see vite.config.ts comment).
 *
 * VITE_FUND_IMAGES_CF_DOMAIN is used to construct absolute CloudFront image
 * URLs from the relative S3 keys stored in DDB.
 */

const CF_DOMAIN = import.meta.env.VITE_FUND_IMAGES_CF_DOMAIN ?? "";

/**
 * Auth issuer routes (gate-exempt) live under `/api/auth/*`. Same-origin in
 * every deployed env (CloudFront routes `/api/*` to the ALB); overridable via
 * VITE_AUTH_BASE.
 */
const AUTH_BASE = import.meta.env.VITE_AUTH_BASE ?? "/api/auth";

/**
 * Send the user to the issuer login, preserving where they were via `?next=`.
 * Throws to halt the in-flight promise chain while the browser navigates away.
 */
function redirectToLogin(): never {
  const next = window.location.pathname + window.location.search;
  window.location.assign(`${AUTH_BASE}/login?next=${encodeURIComponent(next)}`);
  throw new Error("redirecting to sign in");
}

/**
 * Single-flight refresh: a burst of concurrent 401s triggers exactly ONE
 * `POST /refresh` (which rotates the refresh token + re-mints the short-lived
 * access token); every caller awaits the same result. Exported so the session
 * keepalive (session-keepalive.ts) can drive a proactive pre-expiry refresh.
 */
let refreshInFlight: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${AUTH_BASE}/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": "1" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * Build a CloudFront URL for a fund image key stored in DDB.
 * Returns `undefined` when there's no key or the CloudFront domain isn't
 * configured (e.g. local dev without an env override) so callers can render a
 * graceful fallback instead of an empty `<img src="">` (which makes the browser
 * refetch the page URL).
 */
export function fundImageUrl(key: string | undefined): string | undefined {
  if (!key) return undefined;
  if (!CF_DOMAIN) return undefined;
  return `https://${CF_DOMAIN}/${key}`;
}

/**
 * Fetch from the API and return the raw `Response`, transparently handling the
 * dev/staging session gate (401 → refresh once → retry → login redirect).
 *
 * Exposed so callers that need to branch on status/content-type (e.g. the
 * single-fund lookup, which must tell a definitive "not found" apart from a
 * transient server error) can do so without losing the shared gate logic.
 */
export async function apiFetchResponse(path: string): Promise<Response> {
  // credentials:"include" so the shared `__Secure-cf_at` session cookie rides
  // along on the gated dev/staging hosts. Prod web is open, so it's a harmless
  // no-op there.
  const send = () => fetch(path, { credentials: "include" });

  let res = await send();

  // On dev/staging the session gate is ON: a 401 means the short-lived access
  // token expired (or the cookie is missing). Refresh ONCE, then retry the
  // original request. If it still 401s the session is truly gone → bounce to the
  // issuer login. (Prod web is open, so a 401 shouldn't occur there at all.)
  if (res.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) res = await send();
    if (res.status === 401) redirectToLogin();
  }

  return res;
}

/**
 * Fetch a JSON resource from the API. Throws on non-2xx responses so TanStack
 * Query can surface them as errors without extra boilerplate per call-site.
 */
export async function apiFetch<T>(path: string): Promise<T> {
  const res = await apiFetchResponse(path);

  if (!res.ok) {
    throw new Error(`API ${res.status} for ${path}`);
  }
  return res.json() as Promise<T>;
}
